// FASE 3.5 (3.4) — List detail page (/shadow/listas/:listId).
// Shows members + filtered feed. Owner can add/remove members.
import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, X as XIcon, Globe, Lock, Users } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { AnalysisCard } from "../components/AnalysisCard";
import { API, safeJson } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { tFases } from "../i18n/shadow-fases";

export default function ShadowListDetailPage() {
  const { listId } = useParams();
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const [list, setList] = useState(null);
  const [members, setMembers] = useState([]);
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [addUsername, setAddUsername] = useState("");
  const [err, setErr] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = useCallback(async () => {
    const [lr, fr] = await Promise.all([
      fetch(`${API}/shadow/lists/${listId}`, { credentials: "include", headers }),
      fetch(`${API}/shadow/lists/${listId}/feed`, { credentials: "include", headers }),
    ]);
    const ld = await safeJson(lr);
    const fd = await safeJson(fr);
    if (lr.ok && ld) { setList(ld.list); setMembers(ld.members || []); }
    if (fr.ok && fd) setFeed(fd.analyses || []);
    setLoading(false);
  }, [listId, token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const addMember = async (e) => {
    e?.preventDefault?.();
    if (!addUsername.trim()) return;
    setAdding(true); setErr(null);
    try {
      const r = await fetch(`${API}/shadow/lists/${listId}/members`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json", ...headers },
        body: JSON.stringify({ username: addUsername.trim().toLowerCase() }),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail || "Error añadiendo");
      setAddUsername("");
      await load();
    } catch (e2) { setErr(e2.message); }
    finally { setAdding(false); }
  };

  const removeMember = async (memberId) => {
    if (!window.confirm(tFases(lang, "ld_remove_confirm"))) return;
    await fetch(`${API}/shadow/lists/${listId}/members/${memberId}`, {
      method: "DELETE", credentials: "include", headers,
    });
    await load();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white">
        <Navbar />
        <section className="mx-auto max-w-4xl px-6 pt-32 pb-24">
          <Loader2 className="h-5 w-5 animate-spin text-white/45" />
        </section>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-black text-white" data-testid="list-not-found">
        <Navbar />
        <section className="mx-auto max-w-2xl px-6 pt-40 pb-24 text-center">
          <p className="text-xl font-bold">{tFases(lang, "ld_not_found")}</p>
          <Link to="/shadow/listas" className="mt-4 inline-block text-xs text-cyan-300 underline">{tFases(lang, "ld_back_my_lists")}</Link>
        </section>
        <Footer />
      </div>
    );
  }

  const Icon = list.visibility === "private" ? Lock : Globe;

  return (
    <div className="min-h-screen bg-black text-white" data-testid="list-detail-page">
      <Navbar />
      <section className="mx-auto max-w-5xl px-6 pt-32 pb-24">
        <Link to="/shadow/listas" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.22em] text-white/40 hover:text-cyan-300" data-testid="list-back">
          <ArrowLeft className="h-3.5 w-3.5" /> {tFases(lang, "ld_back_my")}
        </Link>

        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span
              className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[9px] font-black uppercase tracking-wider text-white/55"
            >
              <Icon className="h-2.5 w-2.5" /> {list.visibility === "private" ? tFases(lang, "ls_vis_private") : tFases(lang, "ls_vis_public")}
            </span>
            <h1 className="mt-2 font-display text-4xl font-black tracking-tight md:text-5xl" data-testid="list-title">{list.name}</h1>
            {list.description && <p className="mt-2 max-w-2xl text-sm text-white/55">{list.description}</p>}
            <p className="mt-2 text-[11px] text-white/45">
              {tFases(lang, "ld_by")} <Link to={`/shadow/perfil/${list.owner_username}`} className="text-cyan-300 hover:underline">@{list.owner_username}</Link> · {tFases(lang, "ls_members_count", { n: list.member_count })}
            </p>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-[300px_1fr]">
          {/* Members */}
          <aside className="rounded-2xl border border-white/10 bg-white/[0.025] p-4 backdrop-blur lg:sticky lg:top-24 lg:self-start lg:max-h-[80vh] lg:overflow-y-auto">
            <h2 className="flex items-center gap-2 text-sm font-bold uppercase tracking-[0.18em] text-white/75">
              <Users className="h-4 w-4" /> {tFases(lang, "ld_members")}
            </h2>

            {list.is_owner && (
              <form onSubmit={addMember} className="mt-3 flex items-center gap-1">
                <input
                  value={addUsername}
                  onChange={(e) => setAddUsername(e.target.value)}
                  placeholder={tFases(lang, "ld_member_ph")}
                  className="flex-1 rounded-lg border border-white/10 bg-black/40 px-3 py-2 text-xs outline-none focus:border-cyan-400/50"
                  data-testid="list-add-input"
                />
                <button type="submit" disabled={adding || !addUsername.trim()} className="inline-flex items-center gap-1 rounded-lg bg-cyan-400 px-3 py-2 text-[10px] font-black uppercase tracking-wider text-black disabled:opacity-50" data-testid="list-add-btn">
                  {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                </button>
              </form>
            )}
            {err && <p className="mt-2 text-[11px] text-rose-300" data-testid="list-add-error">{err}</p>}

            {members.length === 0 ? (
              <p className="mt-4 text-[11px] text-white/45">{tFases(lang, "ld_members_empty")}</p>
            ) : (
              <ul className="mt-3 space-y-1">
                {members.map((m) => (
                  <li key={m.id} className="group flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 hover:bg-white/[0.04]" data-testid={`list-member-${m.id}`}>
                    <Link to={`/shadow/perfil/${m.username}`} className="flex min-w-0 items-center gap-2">
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-orange-500 to-amber-600 text-[10px] font-black text-black">
                        {(m.name || m.username || "?").trim().charAt(0).toUpperCase()}
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-xs text-white/85">@{m.username}</span>
                        <span className="truncate text-[10px] text-white/45">{m.name}</span>
                      </span>
                    </Link>
                    {list.is_owner && (
                      <button
                        onClick={() => removeMember(m.id)}
                        className="invisible rounded p-1 text-white/40 hover:text-rose-300 group-hover:visible"
                        aria-label="Quitar"
                        data-testid={`list-remove-${m.id}`}
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Feed */}
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.18em] text-white/75">{tFases(lang, "ld_feed")}</h2>
            {feed.length === 0 ? (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.025] p-10 text-center backdrop-blur">
                <p className="text-sm text-white/55">
                  {members.length === 0
                    ? tFases(lang, "ld_feed_empty_add")
                    : tFases(lang, "ld_feed_empty_noposts")}
                </p>
              </div>
            ) : (
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {feed.map((a) => (
                  <AnalysisCard key={a.id} analysis={a} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}
